import React, { useEffect, useMemo, useState } from 'react';
import './Gallery.css';

const GALLERY_PHOTOS = [
  { id: 1, category: 'candid', url: '/images/gallery/candid_1.JPG', alt: 'Candid photography session' },
  { id: 2, category: 'candid', url: '/images/gallery/candid_2.JPG', alt: 'Natural moment capture' },
  { id: 3, category: 'candid', url: '/images/gallery/candid_3.JPG', alt: 'Emotional candid shot' },
  { id: 4, category: 'candid', url: '/images/gallery/candid_4.JPG', alt: 'Lifestyle candid photography' },
  { id: 5, category: 'candid', url: '/images/gallery/candid_5.JPG', alt: 'Real moments captured' },
  { id: 6, category: 'candid', url: '/images/gallery/candid_6.JPG', alt: 'Candid wedding moments' },
  { id: 7, category: 'birthday', url: '/images/gallery/birthday_1.JPG', alt: 'Birthday celebration' },
  { id: 8, category: 'birthday', url: '/images/gallery/birthday_2.JPG', alt: 'Birthday party shoot' },
  { id: 9, category: 'birthday', url: '/images/gallery/birthday_3.JPG', alt: 'Special birthday moment' },
  { id: 10, category: 'birthday', url: '/images/gallery/birthday_4.JPG', alt: 'Birthday cake celebration' },
  { id: 13, category: 'traditional', url: '/images/gallery/traditional_1.JPG', alt: 'Traditional ceremony' },
  { id: 14, category: 'traditional', url: '/images/gallery/traditional_2.JPG', alt: 'Traditional rituals' },
  { id: 15, category: 'traditional', url: '/images/gallery/traditional_3.JPG', alt: 'Cultural traditions' },
  { id: 16, category: 'traditional', url: '/images/gallery/traditional_4.JPG', alt: 'Traditional wedding' },
  { id: 17, category: 'traditional', url: '/images/gallery/traditional_5.JPG', alt: 'Festive traditions' },
  { id: 18, category: 'traditional', url: '/images/gallery/traditional_6.JPG', alt: 'Traditional photography' },
  ];

const Gallery = () => {
  const [galleryFilter, setGalleryFilter] = useState('all');
  const [failedImages, setFailedImages] = useState(new Set());
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [shouldAnimateGrid, setShouldAnimateGrid] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const handleImgError = (id) => {
    setFailedImages(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  useEffect(() => {
    setShouldAnimateGrid(false);
    setShowAll(false); // Reset showAll when filter changes
    const frame = requestAnimationFrame(() => setShouldAnimateGrid(true));

    return () => cancelAnimationFrame(frame);
  }, [galleryFilter]);

  const handleImgLoad = (id) => {
    setLoadedImages(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const filteredPhotos = useMemo(() => (
    galleryFilter === 'all'
      ? GALLERY_PHOTOS
      : GALLERY_PHOTOS.filter(photo => photo.category === galleryFilter)
  ), [galleryFilter]);

  const displayedPhotos = useMemo(() => (
    showAll ? filteredPhotos : filteredPhotos.slice(0, 8)
  ), [filteredPhotos, showAll]);

  return (
    <section id="gallery" className="gallery-section">
      <div className="container">
        <h2>Our Studios Gallery</h2>
        <div className="gallery-filters">
          <button 
            className={galleryFilter === 'all' ? 'active' : ''} 
            onClick={() => setGalleryFilter('all')}
          >
            All
          </button>
          <button 
            className={galleryFilter === 'candid' ? 'active' : ''} 
            onClick={() => setGalleryFilter('candid')}
          >
            Candid
          </button>
          <button 
            className={galleryFilter === 'birthday' ? 'active' : ''} 
            onClick={() => setGalleryFilter('birthday')}
          >
            Birthday
          </button>
          <button 
            className={galleryFilter === 'traditional' ? 'active' : ''} 
            onClick={() => setGalleryFilter('traditional')}
          >
            Traditional
          </button>
        </div>
        <div className={`gallery-grid${shouldAnimateGrid ? ' gallery-grid--animate' : ''}`}>
          {displayedPhotos.map((photo, index) => {
            const hasFailed = failedImages.has(photo.id);
            const isLoaded = loadedImages.has(photo.id);
            const shouldShowLoader = !isLoaded && !hasFailed;
            const placeholderText = hasFailed
              ? `${photo.category} Photo ${photo.id}`
              : 'Loading...';

            return (
              <div
                key={photo.id}
                className={`gallery-item${shouldShowLoader ? ' gallery-item--loading' : ''}`}
                style={{ '--delay': `${index * 60}ms` }}
              >
                {!hasFailed && (
                <img
                  src={photo.url}
                  alt={photo.alt}
                    onLoad={() => handleImgLoad(photo.id)}
                  onError={() => handleImgError(photo.id)}
                    className={`gallery-image${isLoaded ? ' is-visible' : ''}`}
                />
              )}
                {(shouldShowLoader || hasFailed) && (
                  <div className="placeholder-image" aria-hidden={!hasFailed}>
                    {placeholderText}
                  </div>
                )}
            </div>
            );
          })}
        </div>
        {filteredPhotos.length > 6 && !showAll && (
          <div className="view-more-container">
            <button className="view-more-btn" onClick={() => setShowAll(true)}>
              View More
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
